/**
 * /pilot-request — public form to request a paid CBAM Readiness Pilot.
 * Stores the request and notifies admin@veridex-one.com.
 */
import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Rocket, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_OPTIONS = [
  { id: 'CBAM',           label: 'CBAM quarterly reports' },
  { id: 'ICS2',           label: 'ICS2 ENS customs filings' },
  { id: 'PCF',            label: 'Product Carbon Footprint calculation' },
  { id: 'Supplier Portal',label: 'Supplier data portal' },
  { id: 'SAP/DATEV',      label: 'SAP / DATEV integration' },
];

export default function PilotConsultation() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    suppliers_count: '',
    message: '',
  });
  const [modules, setModules] = useState({
    CBAM: true, ICS2: false, PCF: true,
    'Supplier Portal': false, 'SAP/DATEV': false,
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleMod = (id) => setModules((m) => ({ ...m, [id]: !m[id] }));

  const valid =
    form.company_name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error('Please provide a valid company name and work email.'); return; }
    setSubmitting(true);
    try {
      const selectedModules = Object.entries(modules).filter(([, v]) => v).map(([k]) => k);
      await axios.post(`${API}/pilots`, {
        company_name: form.company_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_name: form.contact_name.trim(),
        suppliers_count: form.suppliers_count ? parseInt(form.suppliers_count, 10) : null,
        modules_interest: selectedModules,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit pilot request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="pilot-success">
        <Card className="max-w-lg p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Pilot request received</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Thanks — a Veridex specialist will contact you within <strong>2 business days</strong> to
            schedule the CBAM Readiness Pilot for <strong>{form.company_name}</strong>.
          </p>
          <Button onClick={() => navigate('/pricing')} data-testid="back-to-pricing-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to pricing
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pilot-request-page">
      <header className="border-b border-border bg-card/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/"><Logo size={30} variant="full" /></Link>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="pilot-back-pricing">
            &larr; Back to pricing
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
            <Rocket className="w-3.5 h-3.5" /> Paid Pilot Program
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            Book Your CBAM Readiness Pilot
          </h1>
          <p className="mt-3 text-muted-foreground">
            A 4-week, fixed-scope engagement that takes your company from scattered supplier data
            to an audit-ready CBAM quarterly report. Includes setup, training, and a live
            dry-run with your first 10 imported SKUs.
          </p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
            {[
              'Priced from €3,000 for the first 60–90 days',
              'Dedicated Veridex compliance specialist',
              'Up to 50 supplier invoices imported',
              'CBAM + ICS2 XML end-to-end dry-run',
              'Human‑review audit trail on every report',
              'Credit applied if you convert to an annual plan',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {b}
              </li>
            ))}
          </ul>
        </div>

        <Card className="p-6 sm:p-8">
          <form className="space-y-5" onSubmit={submit} data-testid="pilot-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-company">Company name *</Label>
                <Input id="p-company" required value={form.company_name} onChange={update('company_name')}
                       placeholder="Acme Manufacturing GmbH" data-testid="pilot-company-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-contact">Your name</Label>
                <Input id="p-contact" value={form.contact_name} onChange={update('contact_name')}
                       placeholder="Jane Smith" data-testid="pilot-contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-email">Work email *</Label>
                <Input id="p-email" type="email" required value={form.contact_email} onChange={update('contact_email')}
                       placeholder="jane@acme.com" data-testid="pilot-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-suppliers">Number of suppliers</Label>
                <Input id="p-suppliers" type="number" min="0" value={form.suppliers_count} onChange={update('suppliers_count')}
                       placeholder="e.g. 25" data-testid="pilot-suppliers-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Modules you're interested in</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {MODULE_OPTIONS.map((m) => (
                  <label key={m.id}
                         className="flex items-start gap-2 p-3 rounded-md border border-border hover:bg-muted/30 cursor-pointer"
                         data-testid={`pilot-module-${m.id}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-primary"
                      checked={!!modules[m.id]}
                      onChange={() => toggleMod(m.id)}
                    />
                    <div className="text-sm">
                      <div className="font-medium">{m.id}</div>
                      <div className="text-xs text-muted-foreground">{m.label}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-message">Anything specific you'd like to cover?</Label>
              <Textarea id="p-message" rows={4} value={form.message} onChange={update('message')}
                        placeholder="Current data sources, deadlines, auditor, existing ERP…"
                        data-testid="pilot-message-input" />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground">
                By submitting, you agree to our{' '}
                <Link to="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>{' '}
                and{' '}
                <Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link>.
              </p>
              <Button type="submit" disabled={!valid || submitting} className="bg-primary hover:bg-primary/90" data-testid="pilot-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                {submitting ? 'Submitting…' : 'Request my pilot'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
